import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

let globalAudio: HTMLAudioElement | null = null;

const STREAM_READ_CHUNK_SIZE = 16384;
let activePlaybackToken = 0;
let activeCleanup: (() => void) | null = null;

export function getGlobalAudio() {
  return globalAudio;
}

export function setGlobalAudioVolume(volume: number) {
  if (globalAudio) {
    globalAudio.volume = volume;
  }
}

// Play streamed audio and set initial volume
export async function playStreamedMp3Audio(
  pullStream: SpeechSDK.PullAudioOutputStream,
  isMuted: boolean,
  volume: number,
  onEnded?: () => void,
  _recognitionTimestamp?: number
) {
  const playbackToken = activePlaybackToken + 1;
  stopGlobalAudio();

  return new Promise<void>((resolve, reject) => {
    const mime = 'audio/mpeg';
    const mediaSource = new globalThis.MediaSource();
    const audio = new globalThis.Audio();
    audio.src = URL.createObjectURL(mediaSource);
    audio.muted = isMuted;
    audio.volume = volume;
    audio.autoplay = true;
    audio.preload = 'auto';
    audio.setAttribute('playsinline', 'true');

    activePlaybackToken = playbackToken;
    globalAudio = audio;

    let ended = false;
    const listeners: Array<() => void> = [];
    let sourceBuffer: SourceBuffer | null = null;
    const pendingChunks: ArrayBuffer[] = [];
    let streamExhausted = false;

    const isActive = () => activePlaybackToken === playbackToken && !ended;

    const addListener = (
      target: EventTarget,
      type: string,
      handler: EventListenerOrEventListenerObject
    ) => {
      target.addEventListener(type, handler);
      listeners.push(() => target.removeEventListener(type, handler));
    };

    const cleanup = () => {
      if (ended) return;
      ended = true;
      if (activePlaybackToken === playbackToken) {
        activePlaybackToken += 1;
      }
      while (listeners.length > 0) {
        const remove = listeners.pop();
        remove?.();
      }
      if (globalAudio === audio) {
        globalAudio = null;
      }
      if (activeCleanup === cleanup) {
        activeCleanup = null;
      }
      if (audio.src) {
        URL.revokeObjectURL(audio.src);
      }
      audio.pause();
      audio.src = '';
    };

    const pumpQueue = () => {
      if (!isActive() || !sourceBuffer || mediaSource.readyState !== 'open') {
        return;
      }
      if (sourceBuffer.updating) {
        return;
      }
      if (pendingChunks.length > 0) {
        const nextChunk = pendingChunks.shift();
        if (!nextChunk) return;
        try {
          sourceBuffer.appendBuffer(nextChunk);
        } catch (err) {
          cleanup();
          reject(err instanceof Error ? err : new Error('Failed to append audio buffer'));
        }
        return;
      }
      if (streamExhausted && mediaSource.readyState === 'open') {
        try {
          mediaSource.endOfStream();
        } catch {
          // Ignore double-end or invalid state race during teardown.
        }
      }
    };

    const readStream = async () => {
      while (isActive()) {
        const chunk = new ArrayBuffer(STREAM_READ_CHUNK_SIZE);
        const bytesRead = await pullStream.read(chunk);
        if (!isActive()) {
          return;
        }
        if (bytesRead > 0) {
          const data = chunk.slice(0, bytesRead);
          pendingChunks.push(data);
          pumpQueue();
        } else {
          streamExhausted = true;
          pumpQueue();
          return;
        }
      }
    };

    const onSourceOpen = () => {
      if (!isActive()) {
        return;
      }
      sourceBuffer = mediaSource.addSourceBuffer(mime);
      addListener(sourceBuffer, 'updateend', pumpQueue);
      void readStream().catch((err) => {
        if (!isActive()) {
          return;
        }
        cleanup();
        reject(err instanceof Error ? err : new Error('Failed to read streamed audio'));
      });
    };

    const onEndedInternal = () => {
      if (!isActive()) {
        return;
      }
      if (onEnded) onEnded();
      cleanup();
      resolve();
    };

    const onErrorInternal = (event: Event) => {
      if (!isActive()) {
        return;
      }
      cleanup();
      reject(event instanceof Error ? event : new Error('Audio playback failed'));
    };

    activeCleanup = cleanup;
    addListener(mediaSource, 'sourceopen', onSourceOpen);
    addListener(audio, 'ended', onEndedInternal);
    addListener(audio, 'error', onErrorInternal);

    audio.play().catch((error: unknown) => {
      if (isActive()) {
        cleanup();
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  });
}

export function stopGlobalAudio() {
  activePlaybackToken += 1;
  const cleanup = activeCleanup;
  if (cleanup) {
    cleanup();
    return;
  }
  if (globalAudio) {
    globalAudio.pause();
    globalAudio.src = '';
    globalAudio = null;
  }
}
